import React, { useEffect, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Typography,
  Card,
  Tag,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { proposalApi } from '../../services/proposal';
import type { Customer } from '../../types/proposal';
import type { PaginationParams } from '../../types/api';
import { CustomerForm } from './CustomerForm';
import { ROUTES } from '../../config/routes';
import { SIZE } from '../../constants/design';

const { Title } = Typography;

// 行业选项
const industryOptions = [
  { value: '美妆护肤', label: '美妆护肤' },
  { value: '服饰时尚', label: '服饰时尚' },
  { value: '数码电子', label: '数码电子' },
  { value: '食品饮料', label: '食品饮料' },
  { value: '母婴用品', label: '母婴用品' },
  { value: '家居生活', label: '家居生活' },
  { value: '运动健身', label: '运动健身' },
  { value: '教育培训', label: '教育培训' },
  { value: '游戏娱乐', label: '游戏娱乐' },
  { value: '金融理财', label: '金融理财' },
  { value: '医疗健康', label: '医疗健康' },
  { value: '旅游出行', label: '旅游出行' },
  { value: '其他', label: '其他' },
];

// 状态选项
const statusOptions = [
  { value: 'active', label: '活跃' },
  { value: 'inactive', label: '非活跃' },
];

// 客户路由前缀
const CUSTOMERS_ROUTE = '/customers';

export const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [industryFilter, setIndustryFilter] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [formOpen, setFormOpen] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | undefined>();

  const fetchData = useCallback(async (params?: PaginationParams & { query?: string; industry?: string; status?: string }) => {
    setLoading(true);
    try {
      const response = await proposalApi.getCustomers({
        page: params?.page || pagination.current,
        page_size: params?.page_size || pagination.pageSize,
        query: params?.query,
        industry: params?.industry,
        status: params?.status,
      });
      // 后端返回格式: { data: T[], pagination: { total, page, page_size, total_pages } }
      setData(response.data || []);
      setPagination({
        current: response.pagination.page,
        pageSize: response.pagination.page_size,
        total: response.pagination.total,
      });
    } catch (error) {
      // 错误已在request拦截器中处理
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    fetchData({
      page: 1,
      query: searchKeyword,
      industry: industryFilter,
      status: statusFilter,
    });
  };

  const handleTableChange = (newPagination: { current?: number; pageSize?: number }) => {
    fetchData({
      page: newPagination.current,
      page_size: newPagination.pageSize,
      query: searchKeyword,
      industry: industryFilter,
      status: statusFilter,
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await proposalApi.deleteCustomer(id);
      message.success('客户删除成功');
      fetchData({
        query: searchKeyword,
        industry: industryFilter,
        status: statusFilter,
      });
    } catch (error) {
      message.error('删除客户失败');
    }
  };

  const handleEdit = (id: string) => {
    setEditingCustomerId(id);
    setFormOpen(true);
  };

  const handleCreate = () => {
    setEditingCustomerId(undefined);
    setFormOpen(true);
  };

  const handleFormSuccess = () => {
    fetchData({
      query: searchKeyword,
      industry: industryFilter,
      status: statusFilter,
    });
  };

  const handleRowClick = (record: Customer) => {
    navigate(`${ROUTES.CUSTOMERS}/${record.id}`);
  };

  const columns = [
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (text: string, record: Customer) => (
        <a onClick={() => handleRowClick(record)}>{text}</a>
      ),
    },
    {
      title: '品牌名称',
      dataIndex: 'brand_name',
      key: 'brand_name',
      render: (text: string) => text || '-',
    },
    {
      title: '行业',
      dataIndex: 'industry',
      key: 'industry',
      render: (text: string) => text ? <Tag color="blue">{text}</Tag> : '-',
    },
    {
      title: '联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
      render: (text: string) => text || '-',
    },
    {
      title: '联系邮箱',
      dataIndex: 'contact_email',
      key: 'contact_email',
      render: (text: string) => text || '-',
    },
    {
      title: '联系电话',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      render: (text: string) => text || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleDateString(),
      sorter: true,
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: unknown, record: Customer) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(record.id);
            }}
          >
            编辑
          </Button>
          <Popconfirm
            title="确认删除"
            description="确定要删除此客户吗？删除后不可恢复。"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDelete(record.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4}>客户管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
        >
          新建客户
        </Button>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <Input.Search
            placeholder="搜索公司名称或联系人"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onSearch={handleSearch}
            enterButton={<><SearchOutlined /> 搜索</>}
            style={{ width: SIZE.inputSearch }}
            allowClear
          />
          <Select
            placeholder="选择行业"
            value={industryFilter}
            onChange={(value) => setIndustryFilter(value)}
            onClear={handleSearch}
            onBlur={handleSearch}
            options={industryOptions}
            allowClear
            style={{ width: SIZE.selectLg }}
          />
          <Select
            placeholder="客户状态"
            value={statusFilter}
            onChange={(value) => setStatusFilter(value)}
            onClear={handleSearch}
            onBlur={handleSearch}
            options={statusOptions}
            allowClear
            style={{ width: SIZE.selectMd }}
          />
        </Space>
      </Card>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />

      <CustomerForm
        open={formOpen}
        customerId={editingCustomerId}
        onClose={() => setFormOpen(false)}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
};
